import { useCallback, useRef, useState } from 'react'
import { PhotoType } from '@numeo/types-core'
import { uploadErrorText, uploadRateconToLoad, uploadToLoad, type OpenedRevision, type RevisionChoice } from '../../uploads/uploadFlow'

const KEPT_ORIGINAL = 'Kept the original rate confirmation: the load is unchanged.'
const RESOLVED_ELSEWHERE = 'This revision was already resolved on Ongoing Loads.'
const LEFT_PENDING = 'The revision waits for review on Ongoing Loads.'

interface DrawerUploadsInput {
    loadNumber: string
    /** The load's id when known; otherwise the board finds the load by its number. */
    loadId: string | undefined
    /** A file landed on the load (or a revision was accepted): the drawer reads the load's row again. */
    onFiled: () => void
    /** A POD was filed: its check runs in the background, so the drawer watches the row until it moves. */
    onPodFiled: () => void
}

/** What the review drawer's uploads show: a revision under review, a con filed here, and the notice that says what came of it. */
export interface DrawerUploads {
    /** The revision a rate con just opened, reviewed in the drawer under the rate confirmation row. */
    revision: OpenedRevision | null
    /** A rate con was filed (or its revision accepted) here: the row shows it matched before the next read says so. */
    rateConFiled: boolean
    /** What came of the last rate con upload when it needs saying: a refusal, a mismatch, a revision kept or left. */
    notice: string | null
    uploadRatecon: (file: File) => Promise<void>
    uploadPod: (file: File) => Promise<void>
    revisionChosen: (choice: RevisionChoice) => void
    closeRevision: () => void
}

/**
 * The review drawer's own uploads (v17: a document is uploaded inside the drawer and the drawer reflects it). The file goes through the
 * board's upload, as the chat's do, but nothing is said to the chat and no agent turn starts: what came of it is shown here.
 */
export const useDrawerUploads = ({ loadNumber, loadId, onFiled, onPodFiled }: DrawerUploadsInput): DrawerUploads => {
    const [revision, setRevision] = useState<OpenedRevision | null>(null)
    const [rateConFiled, setRateConFiled] = useState(false)
    const [notice, setNotice] = useState<string | null>(null)
    // Whether the revision on show was decided: closing it undecided leaves it pending on the board, and the notice says so.
    const decided = useRef(false)

    const uploadRatecon = useCallback(
        async (file: File) => {
            setNotice(null)
            try {
                const filed = await uploadRateconToLoad({ loadNumber, loadId, file })
                if (filed.kind === 'revision') {
                    decided.current = false
                    return setRevision(filed.revision)
                }
                if (filed.kind === 'filed') setRateConFiled(true)
                setNotice(filed.notice)
                onFiled()
            } catch (error) {
                setNotice(uploadErrorText(error))
            }
        },
        [loadNumber, loadId, onFiled]
    )

    const uploadPod = useCallback(
        async (file: File) => {
            await uploadToLoad({ loadNumber, loadId, type: PhotoType.POD, file })
            onPodFiled()
        },
        [loadNumber, loadId, onPodFiled]
    )

    const revisionChosen = useCallback(
        (choice: RevisionChoice) => {
            decided.current = true
            if (choice.kind === 'accepted') {
                setRateConFiled(true)
                setNotice(choice.warning ?? null)
                onFiled()
            } else if (choice.kind === 'kept') setNotice(KEPT_ORIGINAL)
            else if (choice.kind === 'gone') {
                setNotice(RESOLVED_ELSEWHERE)
                onFiled()
            }
        },
        [onFiled]
    )

    const closeRevision = useCallback(() => {
        if (!decided.current) setNotice(LEFT_PENDING)
        setRevision(null)
    }, [])

    return { revision, rateConFiled, notice, uploadRatecon, uploadPod, revisionChosen, closeRevision }
}
